import { FileText, Send, CheckCircle2, Ban, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import MainLayout from "@/components/layouts";
import { useParams, Link } from "react-router";
import { useInvoice, useUpdateInvoiceStatus } from "../invoices/invoices.hooks";
import { getInvoice } from "../invoices/invoices.slice";
import { invoice_statuses } from "@/models/invoice.model.ts";
import { format } from "date-fns";
import { getInvoiceSubject } from "@/lib/invoices/invoice-subject";
import { invoiceBreadcrumb } from "@/lib/invoices/invoice-breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { uuid } from "@/interfaces";
import { useState, type ReactElement } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { emailInvoice } from "../invoices/invoices.api";
import { InvoiceBillTo, InvoiceLineItemsTable, InvoicePdfButton } from "@/components/shared/invoices/invoice-sections";
import { useAppDispatch } from "@/lib/store";
import { toast } from "sonner";
import { refusedByTheApi } from "@shyne/shared/api";

const statusMap: Record<invoice_statuses, { color: string; icon: ReactElement; label: string }> = {
  [invoice_statuses.draft]: {
    color: "bg-gray-100 text-gray-700",
    icon: <FileText className="w-3 h-3 mr-1" />,
    label: "Draft",
  },
  [invoice_statuses.sent]: {
    color: "bg-blue-100 text-blue-700",
    icon: <Send className="w-3 h-3 mr-1" />,
    label: "Sent",
  },
  [invoice_statuses.paid]: {
    color: "bg-green-100 text-green-700",
    icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
    label: "Paid",
  },
  [invoice_statuses.void]: {
    color: "bg-red-100 text-red-700",
    icon: <Ban className="w-3 h-3 mr-1" />,
    label: "Void",
  },
};

function StatusBadge({ status }: { status: invoice_statuses }) {
  const { color, icon, label } = statusMap[status] ?? {
    color: "bg-gray-100 text-gray-700",
    icon: <FileText className="w-3 h-3 mr-1" />,
    label: status,
  };
  return (
    <span className={cn("inline-flex items-center px-2 py-1 rounded-sm text-[10px] uppercase select-none", color)}>
      {icon}
      {label}
    </span>
  );
}

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: uuid }>();
  const dispatch = useAppDispatch();
  const { invoice, loading, error } = useInvoice(invoiceId || null);
  const { updateStatus } = useUpdateInvoiceStatus();
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const breadcrumb = invoiceBreadcrumb({ invoice, loading, error });

  if (!invoiceId) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">Invoice ID is required</p>
        </div>
      </MainLayout>
    );
  }

  if (loading && !invoice) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <Card className="max-w-4xl mx-auto space-y-6 p-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-64 w-full" />
          </div>
        </Card>
      </MainLayout>
    );
  }

  if (!invoice) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <Card className="max-w-4xl mx-auto space-y-6 p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-destructive mb-2">{error || "Invoice not found"}</p>
              <Link to="/invoices" className="text-primary hover:underline">
                Back to Invoices
              </Link>
            </div>
          </div>
        </Card>
      </MainLayout>
    );
  }

  const subject = getInvoiceSubject(invoice);

  const handleMarkAsPaid = async () => {
    await updateStatus(invoice.id, invoice_statuses.paid);
    setPaidDialogOpen(false);
  };

  const handleSendInvoice = async () => {
    if (!invoice) return;

    const billingEmail = invoice.billing_email;
    if (!billingEmail) {
      alert(
        "No billing email found for this invoice. Please ensure the invoice's customer has a billing email configured."
      );
      return;
    }

    setSendingEmail(true);
    try {
      await emailInvoice(invoice.id, billingEmail);
    } catch (error) {
      console.error("Failed to send invoice email:", error);
      alert(refusedByTheApi(error) ? error.message : "Failed to send invoice email. Please try again.");
      if (refusedByTheApi(error) && error.code === "invalid") dispatch(getInvoice(invoice.id));
      return;
    } finally {
      setSendingEmail(false);
    }
    setSendDialogOpen(false);
    dispatch(getInvoice(invoice.id))
      .unwrap()
      .catch(() => toast.error("The invoice was sent, but the page could not be refreshed. Reload it to see its status."));
  };

  const handleOpenSendDialog = () => {
    if (invoice) {
      const billingEmail = invoice.billing_email;
      if (!billingEmail) {
        alert(
          "No billing email found for this invoice. Please ensure the invoice's customer has a billing email configured."
        );
        return;
      }
      setSendDialogOpen(true);
    }
  };

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <Card className="max-w-4xl mx-auto space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Invoice #{invoice.invoice_number}</h2>
            {subject.href && subject.shortId && (
              <p className="text-sm text-muted-foreground">
                {subject.presentation.referenceLabel}:{" "}
                <Link to={subject.href} className="text-primary hover:underline">
                  {subject.shortId}
                </Link>
                {subject.presentation.referenceSuffix && <> · {subject.presentation.referenceSuffix}</>}
              </p>
            )}
          </div>
          <StatusBadge status={invoice.status} />
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-none">
            <CardContent>
              <InvoiceBillTo invoice={invoice} />

              <p className="text-sm text-muted-foreground uppercase mt-6">Property:</p>
              {subject.propertyName ? (
                <>
                  <p className="font-medium mt-2">{subject.propertyName}</p>
                  {subject.unitNumber && <p className="text-sm text-muted-foreground">Unit: {subject.unitNumber}</p>}
                </>
              ) : (
                <p className="text-muted-foreground mt-2">N/A</p>
              )}

              <p className="text-sm text-muted-foreground mt-4">
                Issue Date: {invoice.issue_date ? format(new Date(invoice.issue_date), "MMM dd, yyyy") : "N/A"}
              </p>
              <p className="text-sm text-muted-foreground">
                Due Date: {invoice.due_date ? format(new Date(invoice.due_date), "MMM dd, yyyy") : "N/A"}
              </p>
              {invoice.paid_at && (
                <p className="text-sm text-muted-foreground">
                  Paid At: {format(new Date(invoice.paid_at), "MMM dd, yyyy")}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-none">
            <CardContent>
              <p className="text-sm text-muted-foreground uppercase mb-4">Service Details:</p>
              {subject.presentation.detailServiceTitle ? (
                <>
                  <p className="font-medium">{subject.presentation.detailServiceTitle}</p>
                  {subject.presentation.detailServiceDescription && (
                    <p className="text-sm text-muted-foreground">{subject.presentation.detailServiceDescription}</p>
                  )}
                  {subject.presentation.detailLinkLabel && subject.href && (
                    <p className="text-sm mt-2">
                      <Link to={subject.href} className="text-primary hover:underline">
                        {subject.presentation.detailLinkLabel}
                      </Link>
                    </p>
                  )}
                  {subject.assignedName && (
                    <p className="text-sm text-muted-foreground mt-2">Assigned: {subject.assignedName}</p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">N/A</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceLineItemsTable invoice={invoice} />
          </CardContent>
        </Card>

        <Card className="border-none">
          <CardContent>
            <p className="text-sm text-muted-foreground uppercase mb-4">Payment Terms:</p>
            <p className="text-sm text-muted-foreground">
              Please make the payment before{" "}
              {invoice.due_date ? format(new Date(invoice.due_date), "MMM dd, yyyy") : "the due date"}
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-2 justify-end">
          {invoice.status === invoice_statuses.draft && (
            <Button variant="outline" size="sm" onClick={handleOpenSendDialog}>
              <Send className="w-4 h-4 mr-2" /> Send
            </Button>
          )}
          {invoice.status !== invoice_statuses.paid && invoice.status !== invoice_statuses.void && (
            <Button size="sm" onClick={() => setPaidDialogOpen(true)}>
              <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
            </Button>
          )}
          <InvoicePdfButton invoice={invoice} label="Download" />
        </div>

        <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark as Paid</DialogTitle>
              <DialogDescription>
                Are you sure you want to mark invoice #{invoice.invoice_number} as paid?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaidDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleMarkAsPaid}>Mark as Paid</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Invoice</DialogTitle>
              <DialogDescription>
                Send invoice #{invoice.invoice_number} to the billing email address.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Email Address</p>
                <div className="p-3 bg-muted rounded-md border">
                  <p className="text-sm">{invoice.billing_email || "No email found"}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendDialogOpen(false)} disabled={sendingEmail}>
                Cancel
              </Button>
              <Button onClick={handleSendInvoice} disabled={sendingEmail || !invoice.billing_email}>
                {sendingEmail ? "Sending..." : "Send Invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </MainLayout>
  );
}
